/* eslint-disable react-hooks/exhaustive-deps */
import { Box, CircularProgress, Divider, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { default as React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSearchResults } from '../../../../../store/redux-store/global-search/selectors';
import { SideDrawerDynamicContent } from 'app/components/Common/Drawer/SidePanelDynamicContent';
import { PanelControls } from 'app/components/Common/Drawer/SidePanel';
import { selectedTaskGridDataItems } from '../../store/Tasks.selectors';
import { globalSearchActions } from '../../../../../store/redux-store/global-search/slice';
import { taskDetailsActions, taskDetailsSliceKey } from '../../store/Tasks.redux';
import { TaskDetails } from '@AMIEWEB/Tasks/TaskDetails/TaskDetails';
import CommentForm from '@AMIEWEB/Tasks/TaskDetails/Comments/CommentForm';
import SearchSuggestionIcon from '../../../../../app/assets/images/Tasks/SearchSuggestion.svg';
import { useInjectSaga } from 'redux-injectors';
import { taskDetailsSaga } from '@AMIEWEB/Tasks/store/Tasks.saga';
import { usePromiseTracker } from 'react-promise-tracker';
import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import Comment from '../../TaskDetails/Comments/Comment';
import { WhiteTooltip } from '@AMIEWEB/Common/Tooltips/WhiteTooltip';

const useStyles = makeStyles()(theme => ({
  MuiCircularProgressRoot: {
    left: '45%',
    position: 'absolute',
    top: '50vh',
  },
  messageBar: {
    bottom: 0,
    position: 'absolute',
    left: '0px',
    right: '0px',
  },
}));

export const CommentDrawerWrapper = () => {
  useInjectSaga({ key: taskDetailsSliceKey, saga: taskDetailsSaga });
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { promiseInProgress: isCallingListAPI } = usePromiseTracker({ area: 'get-task-list', delay: 0 });
  const { promiseInProgress: isCallingSaveAPI } = usePromiseTracker({ area: 'save-task-detail-comment', delay: 0 });
  const { drawerData } = useSelector(selectSearchResults);

  const tasks = useSelector(selectedTaskGridDataItems);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const isSubTask = drawerData?.data?.selectedorder?.row?.originalTaskId !== 0;

  const taskId = isSubTask
    ? drawerData?.data?.selectedorder?.row?.originalTaskId
    : drawerData?.data?.selectedorder?.row?.taskId;

  const isCallingAPI = isCallingListAPI || isCallingSaveAPI;
  const comments = tasks?.find(task => task.taskId === taskId)?.comments;

  const subTaskOrNoComments = isSubTask || (comments && comments?.length === 0);

  const handleClose = () => {
    dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
    dispatch(taskDetailsActions.setTaskUpdatedId(null));
  };

  const highlightRow = rowId => {
    dispatch(gridStateActions.setHighlightedRow(rowId));
  };

  useEffect(() => {
    if (drawerData?.data) {
      let index = drawerData.data.selectedIndex;
      highlightRow(`${tasks[index]?.placementId}-${index}`);
    }
  }, [drawerData]);

  const headerComponent = () => {
    return (
      <>
        <Typography variant="body1" fontWeight={'bold'}>
          {`${t('notification.taskDetails.comments')} `} &nbsp;
        </Typography>
        <Typography variant="body1">{`(${comments?.length ?? 0}) -`} &nbsp;</Typography>
        <WhiteTooltip title={drawerData.data.selectedorder.row.task}>
          <Link
            sx={{
              whiteSpace: 'nowrap',
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            onClick={event => {
              event.preventDefault();
              setDetailsModalOpen(true);
            }}
            href="#"
          >
            {drawerData.data.selectedorder.row.task}
          </Link>
        </WhiteTooltip>
      </>
    );
  };
  const commentComponent = () => {
    return (
      <>
        <Grid
          container
          sx={{
            padding: '24px 12px 24px 24px',
            maxHeight: '590px',
            height: `${subTaskOrNoComments ? '80%' : 'none'}`,
            overflowY: 'scroll',
          }}
        >
          {subTaskOrNoComments ? (
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: 'inherit',
              }}
            >
              <Grid>
                <img alt={`Search Suggestion Icon`} src={SearchSuggestionIcon} width={'154px'} height={'120px'} />
              </Grid>
              <Grid>
                <Typography variant="body1" fontStyle={'italic'} sx={{ paddingTop: '24px' }}>
                  {t('notification.taskSidePanel.nothingToSeeHere')}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            comments &&
            comments?.length > 0 &&
            comments?.map((comment, index) => {
              return (
                <>
                  <Comment key={index} comment={comment} />{' '}
                  {index < comments.length && (
                    <Box sx={{ height: '2px', width: '100%', padding: '12px 0px' }}>
                      <Divider />
                    </Box>
                  )}
                </>
              );
            })
          )}
        </Grid>
        <Grid className={classes.messageBar}>
          <Divider />
          <Grid
            sx={{
              padding: '20px 24px',
              backgroundColor: theme => theme.palette.components.grid.content.backgroundColor,
            }}
          >
            <CommentForm key={comments?.length} isDisabled={isSubTask} />
          </Grid>
        </Grid>
      </>
    );
  };

  const NavigateTaskCommentDataItem = async direction => {
    if (isCallingAPI) {
      return;
    }
    let index = drawerData.data.selectedIndex;
    if (direction) {
      if (index <= tasks.length - 2) {
        index = index + 1;
      } else {
        index = 0;
      }
    } else {
      if (index > 0) {
        index = index - 1;
      } else {
        index = tasks.length - 1;
      }
    }
    dispatch(
      globalSearchActions.setDrawerData({
        open: drawerData?.open as boolean,
        data: {
          ...drawerData?.data,
          selectedIndex: index,
          selectedorder: {
            row: { ...tasks[index], id: `${tasks[index]?.placementId}-${index}`, task: tasks[index]?.taskName },
          },
        },
      }),
    );

    if (drawerData?.data?.selectedorder?.row?.id) {
      highlightRow(`${tasks[index]?.placementId}-${index}`);
    }
  };

  return (
    <>
      {isCallingAPI ? (
        <CircularProgress className={classes.MuiCircularProgressRoot} />
      ) : (
        <div data-testid="comment-side-panel">
          <SideDrawerDynamicContent
            topHeader={{
              title: `${t('notification.taskDetails.comments')} (${comments?.length ?? 0}) - ${
                drawerData?.data?.selectedorder?.row?.taskCategory
              }`,
              panelControls: new Map<PanelControls, Function>([
                [PanelControls.Close, handleClose],
                [PanelControls.Up, NavigateTaskCommentDataItem],
                [PanelControls.Down, NavigateTaskCommentDataItem],
              ]),
            }}
            headerComponent={headerComponent()}
            closeProfileDrawer={handleClose}
            openProfileDrawer
            isDrawer={true}
            drawerContent={commentComponent()}
            navigate={() => {
              setDetailsModalOpen(true);
            }}
          />
        </div>
      )}
      {detailsModalOpen && (
        <TaskDetails
          taskID={taskId}
          open={detailsModalOpen}
          flag={false}
          setDetailsModalOpen={setDetailsModalOpen}
          handleClose={() => {
            setDetailsModalOpen(false);
          }}
        />
      )}
    </>
  );
};
